import { useState } from "react";
import { useTranslation } from "react-i18next";

interface LocationProps {
  latitude: number;
  longitude: number;
  radius: number;
}

interface LocationState {
  isValid: boolean;
  isLocationLoading: boolean;
  invalidMessage: string;
}

const EARTH_RADIUS_KM = 6371;
const GEOLOCATION_TIMEOUT_MS = 10000;

// 거리 계산 함수
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const toRadians = (degrees: number): number => degrees * (Math.PI / 180);
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return +(EARTH_RADIUS_KM * c).toFixed();
};

const useLocation = ({ latitude, longitude, radius }: LocationProps) => {
  const { t } = useTranslation();
  const [locationState, setLocationState] = useState<LocationState>({
    isValid: false,
    isLocationLoading: false,
    invalidMessage: ""
  });

  const updateLocationState = (newState: Partial<LocationState>) => {
    setLocationState((prevState) => ({ ...prevState, ...newState }));
  };

  const getErrorMessage = (code: number): string => {
    const messages: Record<number, string> = {
      1: t("location_permission_denied"),
      2: t("location_position_unavailable"),
      3: t("location_timeout")
    };
    return messages[code] ?? t("location_default_error");
  };

  const handleCheckLocation = () => {
    updateLocationState({ isLocationLoading: true, invalidMessage: "" });

    if (!navigator.geolocation) {
      updateLocationState({
        isLocationLoading: false,
        invalidMessage: t("location_not_supported")
      });
      return;
    }

    const successCallback: PositionCallback = ({ coords }) => {
      const distanceInKm = calculateDistance(coords.latitude, coords.longitude, latitude, longitude);
      updateLocationState({
        isLocationLoading: false,
        isValid: distanceInKm <= radius,
        invalidMessage: distanceInKm > radius ? t("location_out_of_range", { radius }) : ""
      });
    };

    const errorCallback: PositionErrorCallback = (error) => {
      console.error("Error getting current location:", error.message);
      updateLocationState({
        isLocationLoading: false,
        invalidMessage: getErrorMessage(error.code)
      });
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
      timeout: GEOLOCATION_TIMEOUT_MS
    });
  };

  return {
    ...locationState,
    handleCheckLocation
  };
};

export default useLocation;
